import { useState } from 'react';
import { AppShell, NavLink, Group, Text, ActionIcon, Avatar, Menu, UnstyledButton, Divider, useMantineColorScheme } from '@mantine/core';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IconDashboard,
  IconUsers,
  IconShoppingCart,
  IconStar,
  IconFlag,
  IconLogout,
  IconChevronLeft,
  IconSun,
  IconMoon,
  IconExternalLink,
  IconSeeding,
  IconCategory,
  IconFileImport,
} from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Logo } from './Logo';

const navItems = [
  { icon: IconDashboard, label: 'Dashboard', to: '/admin' },
  { icon: IconUsers, label: 'Users', to: '/admin/users' },
  { icon: IconShoppingCart, label: 'Products', to: '/admin/products' },
  { icon: IconStar, label: 'Reviews', to: '/admin/reviews' },
  { icon: IconFlag, label: 'Moderation', to: '/admin/moderation' },
  { icon: IconCategory, label: 'Categories', to: '/admin/categories' },
  { icon: IconFileImport, label: 'Import Products', to: '/admin/import' },
  { icon: IconSeeding, label: 'Seed Data', to: '/admin/seed' },
];

const externalLinks = [
  { label: 'Sentry', url: 'https://sentry.io' },
  { label: 'Brevo', url: 'https://app.brevo.com' },
  { label: 'Supabase', url: 'https://supabase.com/dashboard' },
];

export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeColorScheme, toggleColorScheme } = useTheme();
  const { setColorScheme } = useMantineColorScheme();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleThemeToggle = () => {
    toggleColorScheme();
    setColorScheme(activeColorScheme === 'light' ? 'dark' : 'light');
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: collapsed ? 80 : 240,
        breakpoint: 'sm',
        collapsed: { mobile: true },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={() => setCollapsed(!collapsed)}
              visibleFrom="sm"
            >
              <IconChevronLeft
                size={20}
                style={{
                  transform: collapsed ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms',
                }}
              />
            </ActionIcon>
            <Logo size="sm" showText={!collapsed} />
            <Text size="sm" c="dimmed" visibleFrom="sm">Admin</Text>
          </Group>

          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              onClick={handleThemeToggle}
              size="lg"
              aria-label="Toggle color scheme"
            >
              {activeColorScheme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Avatar
                      src={user?.profileImageUrl}
                      alt={user?.username}
                      radius="xl"
                      size="sm"
                    />
                    <div style={{ flex: 1 }}>
                      <Text size="sm" fw={500}>
                        {user?.username}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {user?.role === 2 ? 'Admin' : 'Moderator'}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconChevronLeft size={16} />}
                  component={Link}
                  to="/"
                >
                  Back to Site
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                  c="red"
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            component={Link}
            to={item.to}
            label={collapsed ? undefined : item.label}
            leftSection={<item.icon size={20} />}
            active={
              item.to === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.to)
            }
            mb="xs"
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          />
        ))}

        <Divider my="md" label={collapsed ? undefined : 'Services'} />

        {externalLinks.map((link) => (
          <NavLink
            key={link.url}
            component="a"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            label={collapsed ? undefined : link.label}
            leftSection={<IconExternalLink size={16} />}
            mb="xs"
            style={{ borderRadius: 'var(--mantine-radius-md)' }}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
